import { merge, omit, pick, remove, find, findLast } from 'lodash';
import type WboConnectionStatusObserver from '../interfaces/wbo-connection-status-observer.interface';
import GenericQueue from '../util/generic-queue';
import { nowInMicroseconds } from '../util/wbo-utils';
import type {
    WboDelta,
    WboMsg,
    WboChildPointData,
    WboBatchedBroadcastMsg,
    WboAnyAnnotationData,
} from './wbo-annotation.model';

export default class WboPendingChangesQueue implements WboConnectionStatusObserver {
    private readonly outgoingOpsQueue: GenericQueue<WboDelta>;
    private readonly inflightOpsQueue: GenericQueue<WboDelta>;
    private isConnected: boolean;
    private START_COMPOSING_AT: number = 10;
    private MAX_GROUP_SIZE: number = 100;

    constructor(outgoingOpsQueue: GenericQueue<WboDelta>, isConnected = false) {
        this.outgoingOpsQueue = outgoingOpsQueue;
        this.inflightOpsQueue = new GenericQueue<WboDelta>();
        this.isConnected = isConnected;
    }

    /**
     * Packs the queue into a few deltas as possible. This traverse the backing array of the queue and tries to compose
     * deltas that can be composed. For example, if there are two deltas that are updates to the same annotation, they
     *
     */
    pack(): this {
        const deltasArray = this.outgoingOpsQueue.elements();
        this.clear();
        deltasArray.forEach((delta: WboDelta) => {
            this.composeOp(delta);
        });
        return this;
    }

    onSubscribe(): void {}

    setConnected(isConnected: boolean): void {
        this.isConnected = isConnected;
    }

    enqueue(wboDelta: WboDelta): void {
        if (this.isConnected) {
            if (this.outgoingOpsQueue.size() + 1 >= this.START_COMPOSING_AT) {
                if (this.outgoingOpsQueue.size() + 1 === this.START_COMPOSING_AT) {
                    this.pack();
                }
                this.composeOp(wboDelta);
            } else {
                this.outgoingOpsQueue.enqueue(wboDelta);
            }
        } else {
            this.composeOp(wboDelta);
        }
    }

    /**
     * Enqueues the delta at the beginning of the queue. This is useful for re-sending the delta that was just sent to
     * the server, in case the server did not acknowledge it.
     */
    enqueueFirst(wboDelta: WboDelta): void {
        if (this.isConnected) {
            this.outgoingOpsQueue.enqueueFirst(wboDelta);
        } else {
            this.composeOp(wboDelta);
        }
    }

    /**
     * Composes the delta with the rest of the deltas in the queue. This is done by checking the type of the delta and
     * the type of the data in the delta. The composition is done in place, so the delta is modified. The composition
     * is done in the following way:
     * - If the delta is of type 'broadcast' and the data type is 'delete', then the delta is removed from the queue.
     * - If the delta is of type 'broadcast' and the data type is 'update', then the delta is merged with the rest of the
     *  deltas that have the same id.
     *  - If the delta is of type 'broadcast' and the data type is 'child', then the child is added to the parent.
     *  - If the delta is of type 'broadcast' and the data type is 'line' or 'straight', then the delta is just added to
     *  the queue.
     *  - If the delta is of type 'removeAllByUser', then the queue is cleared and the delta is added to the queue.
     *
     */
    private composeOp(delta: WboDelta): void {
        if (!delta) return;
        if (delta.opType === 'broadcast') {
            const msg = delta.msg as WboMsg;
            if (!msg?.data?.type) {
                return;
            }
            switch (msg.data.type) {
                case 'delete':
                    this.composeDelete(msg);
                    break;
                case 'update':
                    this.composeUpdate(msg);
                    break;
                case 'child':
                    this.composeChild(msg);
                    break;
                case 'finish':
                    break;
                case 'line':
                case 'straight': {
                    if (msg?.data?.id) {
                        this.outgoingOpsQueue.enqueue({ opType: delta.opType, msg: delta.msg });
                    }
                    break;
                }
                default:
                    break;
            }
        } else if (delta.opType == 'removeAllByUser') {
            this.composeRemoveAllByUser(delta.msg as WboMsg);
        } else if (delta.opType == 'broadcastMultiple') {
            this.outgoingOpsQueue.enqueue(delta);
            return;
        }
        (delta.msg as WboMsg).operationId = nowInMicroseconds() + this.outgoingOpsQueue.size();
    }

    /**
     * Composition of a delta with opType: 'broadcast' and data type 'delete' just results in the deletion of the
     * referenced delta. If no such delta is found among the pending delta queue, then the deletion delta is just added
     * to the queue, since it might be found in the server side annotation list.
     */
    private composeDelete(msg: WboMsg): void {
        if (!msg?.data?.id) {
            return;
        }
        // Remove the deltas that are to be deleted from the queue, with lodash
        const removedDeltas: WboDelta[] = remove(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
            if (entry.opType !== 'broadcast') {
                return false;
            }
            return (entry.msg as WboMsg).data?.id == msg.data?.id;
        });
        if (!removedDeltas || removedDeltas.length == 0) {
            this.outgoingOpsQueue.enqueue({ opType: 'broadcast', msg });
        }
    }

    private composeUpdate(msg: WboMsg): void {
        if (!msg?.data?.id) {
            return;
        }
        const referencedDelta = find(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
            if (entry.opType !== 'broadcast') {
                return false;
            }
            return (entry.msg as WboMsg).data?.id == msg.data?.id;
        });
        if (!referencedDelta) {
            // TODO: Check if there are any orphan updates  and merge this with that
            this.outgoingOpsQueue.enqueue({ opType: 'broadcast', msg });
            return;
        }
        if (referencedDelta.msg.data && msg.data) {
            merge(
                referencedDelta.msg.data as WboAnyAnnotationData,
                omit(msg.data as WboAnyAnnotationData, ['type', 'tool'])
            );
        }
    }

    private composeChild(msg: WboMsg): void {
        let childPointData = msg?.data as WboChildPointData;
        if (!childPointData?.parent) {
            return;
        }

        const parentDelta = find(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
            if (entry.opType !== 'broadcast') {
                return false;
            }
            return (entry.msg as WboMsg).data?.id == childPointData.parent;
        });
        if (!parentDelta) {
            childPointData = {
                ...childPointData,
                operationId: msg.operationId ?? nowInMicroseconds(),
            } as WboChildPointData;

            // Find the last delta of opType batchedBroadcast of the same parent
            const lastBatchedBroadcast = findLast(this.outgoingOpsQueue.elements(), (entry: WboDelta) => {
                if (entry.opType !== 'broadcastMultiple') {
                    return false;
                }
                const batchedMsg = entry.msg as WboBatchedBroadcastMsg;
                const firstChild = batchedMsg.data?.[0] as WboChildPointData;
                return firstChild?.parent === childPointData.parent;
            });
            if (!lastBatchedBroadcast) {
                this.outgoingOpsQueue.enqueue({
                    opType: 'broadcastMultiple',
                    msg: {
                        board: msg.board,
                        clientId: msg.clientId,
                        data: [childPointData],
                    },
                });
            } else {
                // If the last batched broadcast data array size is greater than MAX_GROUP_SIZE, then create a new
                // batched broadcast
                const lastBatchedBroadcastData = (lastBatchedBroadcast.msg as WboBatchedBroadcastMsg)
                    .data as WboChildPointData[];
                if (lastBatchedBroadcastData.length >= this.MAX_GROUP_SIZE) {
                    this.outgoingOpsQueue.enqueue({
                        opType: 'broadcastMultiple',
                        msg: {
                            board: msg.board,
                            clientId: msg.clientId,
                            data: [childPointData],
                        },
                    });
                } else {
                    lastBatchedBroadcastData.push(childPointData);
                }
            }
            return;
        }
        const parentData = parentDelta.msg.data as WboAnyAnnotationData & WboChildPointData;
        if (msg.data) {
            const childData = pick(msg.data as WboAnyAnnotationData & WboChildPointData, [
                'parent',
                'tool',
                'type',
                'x',
                'y',
            ]) as WboAnyAnnotationData & WboChildPointData;
            if (Array.isArray((parentData as { _children?: unknown[] })._children)) {
                (parentData as { _children: unknown[] })._children.push(childData);
            } else {
                (parentData as { _children: unknown[] })._children = [childData];
            }
        }
    }

    private composeRemoveAllByUser(msg: WboMsg): void {
        this.outgoingOpsQueue.clear();
        this.outgoingOpsQueue.enqueue({ opType: 'removeAllByUser', msg });
    }

    onConnect(): void {
        this.isConnected = true;
    }
    onDisconnect(): void {
        this.isConnected = false;
    }
    onReconnect(): void {
        this.isConnected = true;
    }

    queue(): GenericQueue<WboDelta> {
        return this.outgoingOpsQueue;
    }

    inflightOps(): GenericQueue<WboDelta> {
        return this.inflightOpsQueue;
    }

    clear(): void {
        this.outgoingOpsQueue.clear();
    }

    addInflightOp(wboDelta: WboDelta): void {
        this.inflightOpsQueue.enqueue(wboDelta);
    }

    clearInflightOps(): void {
        this.inflightOpsQueue.clear();
    }
}
